import React from 'react';
import { DataContext } from '../DataProvider';
import styled from 'styled-components';

const SettingsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.46 1.81L14.54 1.81L13.89 4.43L17.61 6.58L19.55 4.71L22.09 9.11L19.5 9.85L19.5 14.15L22.09 14.89L19.55 19.29L17.61 17.42L13.89 19.57L14.54 22.19L9.46 22.19L10.11 19.57L6.39 17.42L4.45 19.29L1.91 14.89L4.5 14.15L4.5 9.85L1.91 9.11L4.45 4.71L6.39 6.58L10.11 4.43Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle
      cx="12"
      cy="12"
      r="3"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

function NavBar() {
  const { currentMode, setMode, muted, setMuted, volume, setVolume } =
    React.useContext(DataContext);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const settingsRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTabClick = (mode) => {
    setMode(mode);
  };

  return (
    <Nav>
      <NavItem
        className={currentMode === 'builder' ? 'active' : ''}
        onClick={() => handleTabClick('builder')}
      >
        Builder
      </NavItem>
      <NavItem
        className={currentMode === 'trainer' ? 'active' : ''}
        onClick={() => handleTabClick('trainer')}
      >
        Trainer
      </NavItem>
      {/* Practice and Library tabs hidden for now */}
      <NavItem
        className={currentMode === 'openingTree' ? 'active' : ''}
        onClick={() => handleTabClick('openingTree')}
      >
        Tree
      </NavItem>
      <Divider />
      <SettingsWrapper ref={settingsRef}>
        <SettingsButton
          onClick={() => setSettingsOpen(!settingsOpen)}
          className={settingsOpen ? 'open' : ''}
          aria-label="Settings"
        >
          <SettingsIcon />
        </SettingsButton>
        {settingsOpen && (
          <SettingsDropdown>
            <DropdownRow>
              <SoundToggle onClick={() => setMuted(!muted)}>
                {muted ? 'Muted' : 'Sound'}
              </SoundToggle>
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                disabled={muted}
              />
              <VolumeLabel>{Math.round(volume * 100)}%</VolumeLabel>
            </DropdownRow>
          </SettingsDropdown>
        )}
      </SettingsWrapper>
    </Nav>
  );
}

export default NavBar;

const Nav = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  padding: 7px;
  border-radius: 999px;
  background: rgba(255, 249, 241, 0.92);
  border: 1px solid var(--border);
  overflow: visible;
`;

const NavItem = styled.button`
  padding: 12px 21px;
  cursor: pointer;
  color: var(--text-soft);
  font-size: 1.23rem;
  border-radius: 999px;
  border: 1px solid transparent;
  background: none;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(159, 109, 76, 0.1);
    color: var(--text);
    transform: translateY(-1px);
  }

  &.active {
    color: var(--accent-strong);
    font-weight: 600;
    background: rgba(159, 109, 76, 0.12);
    border-color: rgba(159, 109, 76, 0.18);
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 30px;
  background: var(--border);
  margin: 0 3px;
  flex-shrink: 0;
`;

const SettingsWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--text-soft);
  background: none;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover,
  &.open {
    background: rgba(159, 109, 76, 0.1);
    color: var(--text);
    border-color: rgba(159, 109, 76, 0.12);
  }
`;

const SettingsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: var(--surface-strong);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 14px 16px;
  z-index: 100;
  min-width: 240px;
`;

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SoundToggle = styled.button`
  padding: 6px 12px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  color: var(--accent-strong);
  background: var(--accent-ghost);
  border: 1px solid rgba(159, 109, 76, 0.22);
  border-radius: 999px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(159, 109, 76, 0.18);
  }
`;

const VolumeSlider = styled.input`
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  background: var(--accent-soft);
  outline: none;
  transition: opacity 0.2s;
  border-radius: 999px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    background: var(--accent);
    cursor: pointer;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(95, 67, 46, 0.28);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: var(--accent);
    cursor: pointer;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(95, 67, 46, 0.28);
    border: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VolumeLabel = styled.span`
  font-size: 0.72rem;
  color: var(--text-soft);
  min-width: 30px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
